import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { ChevronLeftIcon, DeleteIcon, Eye, EyeClosed, PlusCircle, Save, Trash, TriangleAlert, X } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer, Select, Tooltip } from "antd";
import { CUSTOM_CONFIGURATION, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, SALARY_COMPONENT, SALARY_TEMPLATE } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import {
    calculateEarningsAmounts,
    calculateSalaryBreakdown,
    getComponentDisplayAmounts,
    getEsicCalculationHint,
    getPfCalculationHint,
    parseOptionalNumber,
    clampGrossMonthly,
    round2,
    ESI_ALLOWED_LIMIT,
} from "./salaryTemplateCalc";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";

export const default_total_earnings = {
    percentage: 0,
    monthly_amount: 0,
    annual_amount: 0,
}

export const default_total_deduction = {
    percentage: 0, // dedutions percentage of employee
    employee_monthly_amount: 0,
    employer_monthly_amount: 0,
    annual_amount: 0
}

// default states
let initialFormData = {
    id: '',
    is_active: true,
    code: "",
    salary_template_name: "",
    salary_department: "",
    salary_designation: "",
    monthly_ctc: 0,
    annual_ctc: 0,
    earning_components: [],
    total_earning: { ...default_total_earnings },
    deduction_components: [],
    total_deduction: { ...default_total_deduction },
    net_salary: 0,

    // add new components
    component_type: "",
    components: [],
    is_edit: false,
    is_draft: false,
    edit_component: []
}

let initalDropdownList = {
    designationList: [],
    departmentlist: [],
}

const CustomDropdown = ({
    label = "Select",
    list = [],
    displayKey = "name",
    selectedItem,
    setSelectedItem,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [hoveringIcon, setHoveringIcon] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    const filteredList = list.filter((item) =>
        item[displayKey].toLowerCase().includes(search.toLowerCase())
    );

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredList.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredList.length - 1
            );
        } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            setSelectedItem(filteredList[highlightedIndex]);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    // Scroll selected into view
    useEffect(() => {
        if (listRef.current && highlightedIndex !== -1) {
            const el = listRef.current.children[highlightedIndex];
            if (el) el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    return (
        <div ref={dropdownRef} className="relative w-full">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>

            {/* Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                className={cn(
                    "w-full border border-gray-300 dark:border-gray-600 rounded-lg h-10 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-background shadow-sm cursor-pointer flex justify-between items-center transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:border-gray-300 dark:focus:border-gray-600",
                    isOpen && "ring-2 ring-sidebar-primary/40 border-sidebar-primary dark:border-sidebar-primary",
                )}
            >
                <span className={`text-sm ${selectedItem ? "text-black dark:text-white" : "text-gray-400 dark:text-gray-500"}`}>
                    {selectedItem ? selectedItem[displayKey] : `Select ${label.toLowerCase()}`}
                </span>

                {/* Hover-based icon change */}
                <div
                    onMouseEnter={() => setHoveringIcon(true)}
                    onMouseLeave={() => setHoveringIcon(false)}
                    onClick={(e) => {
                        if (hoveringIcon && selectedItem) {
                            e.stopPropagation();
                            setSelectedItem(null);
                            setSearch("");
                            setIsOpen(false);
                        }
                    }}
                    className="ml-2 transition-colors duration-200"
                >
                    {hoveringIcon && selectedItem ? (
                        <Trash className="w-3 h-3 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300" />
                    ) : (
                        <svg
                            className={`w-4 h-4 transform transition-transform duration-200 text-gray-500 dark:text-gray-400 ${isOpen ? "rotate-180" : "rotate-0"}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    )}
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-background border border-gray-200 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50">
                    {/* Search input */}
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setHighlightedIndex(0);
                        }}
                        placeholder="Search..."
                        className="w-full px-3 py-2 border-b dark:border-gray-600 rounded-t-md border-gray-200 text-sm bg-background text-foreground placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40"
                    />
                    <ul ref={listRef} className="max-h-60 overflow-y-auto">
                        {filteredList.length === 0 ? (
                            <li className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500 bg-background">
                                No options found
                            </li>
                        ) : (
                            filteredList.map((item, index) => (
                                <li
                                    key={index}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setIsOpen(false);
                                        setSearch("");
                                        setHighlightedIndex(-1);
                                    }}
                                    className={`px-4 py-2 text-sm cursor-pointer transition-all duration-150
                                ${index === highlightedIndex
                                            ? "bg-sidebar-primary/10 dark:bg-sidebar-primary/20 text-sidebar-primary dark:text-sidebar-primary"
                                            : "bg-background text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        }`}
                                >
                                    {item[displayKey]}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const ComponentTable = ({ components, setComponents, type = "Earnings", monthlyGross, componentList = [], earningComponents, salaryConfig }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [newComponent, setNewComponent] = useState({
        name: "",
        percentage: "",
        calcof: "CTC",
        is_amount: null,
        is_employee_share: null,
        is_employer_share: null,
        employee_share_percentage: type === "Deductions" ? 0 : undefined,
        employer_share_percentage: type === "Deductions" ? 0 : undefined
    });
    const [visibleAmounts, setVisibleAmounts] = useState({});
    const calcConfig = salaryConfig || { pfLimit: 15000, capAmount: 0, esicLimitUpto: ESI_ALLOWED_LIMIT };

    const toggleVisibility = (index) => {
        setVisibleAmounts((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };


    const handleChange = (index, key, value) => {
        const updated = [...components];
        updated[index][key] = value;
        setComponents(updated);
    };

    const handleDelete = (index) => {
        const updated = components.filter((_, i) => i !== index);
        setComponents(updated);
    };

    const handleAdd = () => {
        setIsDrawerOpen(true);
    };

    const calculateAmounts = (component) => getComponentDisplayAmounts(
        component,
        type,
        monthlyGross,
        earningComponents,
        calcConfig,
    );

    const earningTotals = calculateEarningsAmounts(monthlyGross, earningComponents);

    const totalEarningsPercentage = type === "Earnings"
        ? (earningTotals?.totalCTCPercentage ?? 0)
        : 0;
    const totalEarningMonthly = type === "Earnings"
        ? (earningTotals?.totalEarnings ?? 0)
        : 0;
    const totalEarningAnnual = totalEarningMonthly * 12;

    const totalEmployeeDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employee_share_percentage || 0) : acc;
    }, 0);
    const totalEmployerDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employer_share_percentage || 0) : acc;
    }, 0);
    const totalDeductionMonthly = type === "Deductions"
        ? components.reduce((acc, component) => acc + Number(calculateAmounts(component).monthly), 0)
        : 0;
    const totalDeductionAnnual = totalDeductionMonthly * 12;

    // Handle adding a new component
    const handleAddComponent = () => {
        if (!newComponent.name) return;

        const componentToAdd = {
            id: newComponent.id,
            name: newComponent.name,
            calcof: newComponent.calcof,
            ...(type === "Earnings" ? {
                percentage: parseFloat(newComponent.percentage),
                annual_amount: 0,
                monthly_amount: 0
            } : {
                is_amount: newComponent.is_amount,
                is_employee_share: newComponent.is_employee_share,
                is_employer_share: newComponent.is_employer_share,
                employee_share_percentage: parseFloat(newComponent.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(newComponent.employer_share_percentage) || 0,
                monthly_amount: parseFloat(newComponent.monthly_amount) || 0,
                employee_share_monthly_amount: parseFloat(newComponent.monthly_amount || newComponent.employee_share_monthly_amount) || 0,
                employer_share_monthly_amount: 0,
                percentage: parseFloat(newComponent.employee_share_percentage) +
                    parseFloat(newComponent.employer_share_percentage) || 0
            })
        };

        setComponents(prev => [...prev, componentToAdd]);
        setIsDrawerOpen(false);
        setNewComponent({
            name: "",
            percentage: "",
            calcof: "CTC",
            is_amount: type === "Deductions",
            is_employee_share: type === "Deductions",
            is_employer_share: type === "Deductions",
            employee_share_percentage: type === "Deductions" ? 0 : undefined,
            employer_share_percentage: type === "Deductions" ? 0 : undefined
        });
    };

    const hasBasicComponent = earningComponents.some(item => item.name?.toLowerCase() === "basic");
    const hasDAComponent = earningComponents.some(item => item.name?.toLowerCase() === "da");
    const isTotalCTCPercentValid = totalEarningsPercentage?.toFixed(2) === "100.00";

    return (
        <div className="dark:bg-gray-800">
            <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-sidebar-primary dark:bg-sidebar-primary rounded-full"></div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white">{type}</h3>
            </div>
            <p className="text-sm text-muted-foreground dark:text-gray-400 mt-1 mb-2">Define the {type.toLowerCase()} components of the salary.</p>
            {type === 'Earnings' && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
                    Total monthly/annual includes <strong className="font-medium">Of CTC</strong> components only.
                    Rows marked <strong className="font-medium">Of Basic</strong> (e.g. HRA, DA) are a breakdown of Basic and are not added again.
                </p>
            )}

            {type == 'Earnings' && (!hasBasicComponent || !isTotalCTCPercentValid) && (
                <div className="py-2 text-red-400 dark:text-red-300 text-[10px]">
                    {!hasBasicComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">Basic</strong> earning component must exist.</div>
                    )}
                    {!isTotalCTCPercentValid && (
                        <div>⚠️ Total earnings percentage calculated from <strong className="dark:text-red-200">CTC</strong> must be exactly <strong className="dark:text-red-200">100%</strong>.</div>
                    )}
                </div>
            )}

            {type === 'Deductions' && (
                <div className="mb-2 space-y-1">
                    <p className="text-[10px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 rounded px-2 py-1.5">
                        <strong>PF (from configuration):</strong> wage ceiling{' '}
                        <strong>₹{Number(calcConfig.pfLimit || 0).toLocaleString('en-IN')}</strong>
                        {calcConfig.capAmount > 0 && (
                            <>
                                {' '}· fixed share when Basic ≥ ceiling:{' '}
                                <strong>₹{Number(calcConfig.capAmount).toLocaleString('en-IN')}</strong> each (employee & employer)
                            </>
                        )}
                        . PF base = Basic + DA.
                    </p>
                    <p className="text-[10px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 rounded px-2 py-1.5">
                        <strong>ESIC:</strong> applies only when monthly gross is{' '}
                        <strong>₹{Number(calcConfig.esicLimitUpto ?? ESI_ALLOWED_LIMIT).toLocaleString('en-IN')}</strong> or below.
                    </p>
                </div>
            )}

            {type === 'Deductions' && (!hasBasicComponent || !hasDAComponent) && (
                <div className="py-2 text-red-400 dark:text-red-300 text-[10px]">
                    {!hasBasicComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">Basic</strong> earning component must exist.</div>
                    )}
                    {!hasDAComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">DA</strong> earning component must exist.</div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-12 text-sm font-medium text-gray-600 dark:text-gray-400 border-b dark:border-gray-600 mt-1 pb-2">
                <div className="col-span-2">Component Name</div>
                <div className="col-span-1">Of</div>
                <div className="col-span-2">Percentage</div>
                <div className="col-span-3 text-right">Monthly Amount</div>
                <div className="col-span-3 text-right">Annual Amount</div>
                <div className="col-span-1 text-center"> </div>
            </div>

            {components?.map((item, index) => {
                const amounts = calculateAmounts(item);
                const componentKey = item.name?.trim().toLowerCase();
                const pfHint = componentKey === 'pf'
                    ? getPfCalculationHint(
                        earningTotals.basicAmount,
                        earningTotals.daAmount,
                        calcConfig,
                    )
                    : null;
                const esicHint = (componentKey === 'esic' || componentKey === 'esi')
                    ? getEsicCalculationHint(monthlyGross, calcConfig)
                    : null;

                return (
                    <div
                        key={index}
                        className={
                            `grid grid-cols-12 gap-2 text-sm items-center py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-600 last:border-b-0 transition-colors duration-150
                    ${item.calcof === "Basic" && type !== "Deductions" ? "bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white" : ""}`
                        }
                    >
                        <div className="col-span-2">
                            <span className='text-gray-700 dark:text-gray-300 inline-flex items-center gap-1'>
                                {item.name}
                                {(pfHint || esicHint) && (
                                    <Tooltip title={pfHint || esicHint}>
                                        <TriangleAlert className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 cursor-help shrink-0" />
                                    </Tooltip>
                                )}
                            </span>
                        </div>
                        <div className="col-span-1">
                            <span className={`inline-block px-2 py-1 rounded-full font-medium text-xs
                    ${item.calcof === "CTC" ? "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300" :
                                    item.calcof === "Basic" ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" :
                                        "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300"}`}>
                                {item.calcof}
                            </span>
                        </div>

                        {type === "Deductions" ? (
                            <>
                                {
                                    !item.is_employee_share && !item.is_employer_share ? (
                                        <div className='col-span-2 text-gray-600 dark:text-gray-400'>Fixed Amount</div>
                                    ) : (
                                        <>
                                            <div className="col-span-1">
                                                <Tooltip title="Employee Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employee_share_percentage}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            if (raw === "") {
                                                                handleChange(index, "employee_share_percentage", "");
                                                                return;
                                                            }
                                                            let val = Number(raw);
                                                            if (Number.isNaN(val)) return;
                                                            if (val > 100) val = 100;
                                                            if (val < 0) val = 0;
                                                            handleChange(index, "employee_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employee_share}
                                                        className={`w-full px-2 py-1.5 text-sm rounded-md border text-gray-700 dark:text-gray-300 bg-background border-input ${!item.is_employee_share
                                                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed text-gray-500 dark:text-gray-500'
                                                            : 'bg-background border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-sidebar-primary/40'
                                                            } transition-colors duration-200`}
                                                    />
                                                </Tooltip>
                                            </div>
                                            <div className="col-span-1">
                                                <Tooltip title="Employer Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employer_share_percentage}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            if (raw === "") {
                                                                handleChange(index, "employer_share_percentage", "");
                                                                return;
                                                            }
                                                            let val = Number(raw);
                                                            if (Number.isNaN(val)) return;
                                                            if (val > 100) val = 100;
                                                            if (val < 0) val = 0;
                                                            handleChange(index, "employer_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employer_share}
                                                        className={`w-full px-2 py-1.5 text-sm rounded-md border text-gray-700 dark:text-gray-300 bg-background border-input ${!item.is_employer_share
                                                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed text-gray-500 dark:text-gray-500'
                                                            : 'bg-background border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-sidebar-primary/40'
                                                            } transition-colors duration-200`}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </>
                                    )
                                }
                            </>
                        ) : (
                            <div className="col-span-2">
                                <input
                                    type="number"
                                    placeholder="Enter %"
                                    value={item.percentage === 0 || item.percentage ? item.percentage : ""}
                                    onChange={(e) => handleChange(index, "percentage", e.target.value === "" ? "" : e.target.value)}
                                    className="w-full px-2 py-1.5 text-sm rounded-md bg-background border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 transition-colors duration-200"
                                />
                            </div>
                        )}

                        <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-medium flex items-center justify-end gap-2">
                            {(type === 'Deductions' && !item.is_employee_share && !item.is_employer_share) ? (
                                <input
                                    type="number"
                                    placeholder="₹ Enter monthly amount"
                                    value={item.monthly_amount === 0 || item.monthly_amount ? item.monthly_amount : ""}
                                    onChange={(e) => handleChange(index, "monthly_amount", e.target.value === "" ? "" : e.target.value)}
                                    className="w-[100px] px-2 py-1.5 text-sm rounded-md bg-background border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-right focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 transition-colors duration-200"
                                />
                            ) : (
                                <span>
                                    {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                        ? `₹${amounts.employeeMonthly} / ₹${amounts.employerMonthly}`
                                        : `₹${amounts.monthly}`
                                    }
                                </span>
                            )}

                            {item.is_employee_share && item.is_employer_share && (
                                <button
                                    onClick={() => toggleVisibility(index)}
                                    className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                                >
                                    {visibleAmounts[index] ? (
                                        <Eye className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    ) : (
                                        <EyeClosed className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-medium flex items-center justify-end gap-2">
                            <span>
                                {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                    ? `₹${(Number(amounts.employeeMonthly) * 12).toFixed(2)} / ₹${(Number(amounts.employerMonthly) * 12).toFixed(2)}`
                                    : `₹${amounts.annual}`
                                }
                            </span>
                            {item.is_employee_share && item.is_employer_share && (
                                <button
                                    onClick={() => toggleVisibility(index)}
                                    className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                                >
                                    {visibleAmounts[index] ? (
                                        <Eye className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    ) : (
                                        <EyeClosed className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="col-span-1 flex justify-center">
                            <button
                                onClick={() => handleDelete(index)}
                                className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                            >
                                <DeleteIcon className="w-4 h-4 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300" />
                            </button>
                        </div>
                    </div>
                );
            })}

            <button
                onClick={handleAdd}
                className="mt-3 flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
            >
                <PlusCircle className="w-4 h-4" />
                Add {type.slice(0, -1)}
            </button>

            {/* drawer */}
            <Drawer
                title={`Add ${type.slice(0, -1)} Component`}
                placement="right"
                width={400}
                onClose={() => setIsDrawerOpen(false)}
                open={isDrawerOpen}
                className="dark:bg-gray-800 dark:text-white"
                styles={{
                    body: {
                        backgroundColor: 'var(--antd-drawer-bg)',
                        color: 'var(--antd-text)'
                    },
                    header: {
                        backgroundColor: 'var(--antd-drawer-bg)',
                        color: 'var(--antd-text)',
                        borderBottom: '1px solid var(--antd-border-color)'
                    }
                }}
            >
                <div className="flex flex-col gap-4 dark:bg-gray-800">
                    <CustomDropdown
                        label="Component Name"
                        list={componentList}
                        displayKey="salary_component_name"
                        selectedItem={newComponent.name ?
                            { salary_component_name: newComponent.name } :
                            null
                        }
                        setSelectedItem={(value) =>
                            setNewComponent({
                                ...newComponent,
                                id: value.id,
                                name: value.salary_component_name,
                                is_amount: value.amount,
                                is_employee_share: value.employee_share,
                                is_employer_share: value.employer_share
                            })
                        }
                    />

                    <CustomDropdown
                        label="Calculate Of"
                        list={[{ name: "CTC" }, { name: "Basic" }]}
                        selectedItem={{ name: newComponent.calcof }}
                        setSelectedItem={(val) => setNewComponent({ ...newComponent, calcof: val.name })}
                    />

                    {type === "Earnings" && (
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Percentage
                            </label>
                            <input
                                type="number"
                                placeholder='Enter percentage'
                                value={newComponent.percentage}
                                onChange={(e) => setNewComponent({ ...newComponent, percentage: e.target.value })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md h-10 px-3 py-2 text-sm bg-background text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 transition-colors duration-200"
                            />
                        </div>
                    )}

                    {type === "Deductions" && (newComponent.is_amount || (!newComponent.is_employee_share && !newComponent.is_employer_share)) && (
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Monthly Amount
                            </label>
                            <input
                                type="number"
                                placeholder="Enter monthly amount"
                                value={newComponent.monthly_amount ?? ""}
                                onChange={(e) => setNewComponent({ ...newComponent, monthly_amount: e.target.value })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md h-10 px-3 py-2 text-sm bg-background text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 transition-colors duration-200"
                            />
                        </div>
                    )}

                    <Button
                        variant="brand"
                        onClick={handleAddComponent}
                        className="w-full mt-4 transition-colors duration-200 font-medium"
                    >
                        Add Component
                    </Button>
                </div>
            </Drawer>

            <div className="mt-4 border-t dark:border-gray-600 pt-3 flex justify-end text-sm text-gray-700 dark:text-gray-300 font-medium">
                <div className="space-x-6 flex items-center">
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total % (of CTC):{" "}
                                <span
                                    className={`font-semibold ${type === 'Earnings' && totalEarningsPercentage?.toFixed(2) !== "100.00"
                                        ? "text-red-600 dark:text-red-400"
                                        : "text-gray-700 dark:text-gray-300"
                                        }`}
                                >
                                    {totalEarningsPercentage?.toFixed(2)}%
                                </span>
                            </span>
                        ) : (
                            <span className='flex gap-3'>
                                <span>
                                    Total (Employee) %:{" "}
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        {totalEmployeeDeductionsPercentage?.toFixed(2)}%
                                    </span>
                                </span>
                                <span>
                                    Total (Employer) %:{" "}
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        {totalEmployerDeductionsPercentage?.toFixed(2)}%
                                    </span>
                                </span>
                            </span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total Monthly (CTC): <strong className="text-gray-900 dark:text-white">₹{totalEarningMonthly?.toFixed(2)}</strong>
                            </span>
                        ) : (
                            <span>
                                Total Monthly: <strong className="text-gray-900 dark:text-white">₹{totalDeductionMonthly?.toFixed(2)}</strong>
                            </span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total Annual (CTC): <strong className="text-gray-900 dark:text-white">₹{totalEarningAnnual?.toFixed(2)}</strong>
                            </span>
                        ) : (
                            <span>
                                Total Annual: <strong className="text-gray-900 dark:text-white">₹{totalDeductionAnnual?.toFixed(2)}</strong>
                            </span>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

const SalaryTemplateAddEdit = () => {

    //  states
    const [formData, setFormData] = useState(initialFormData);
    const [dropdownList, setDropDownList] = useState(initalDropdownList);

    const [loadings, setLoadings] = useState(false);
    const [selectedDept, setSelectedDept] = useState([]);
    const [selectedDesig, setSelectedDesig] = useState([]);
    const [earningComponents, setEarningComponents] = useState([]);
    const [deductionComponents, setDeductionComponents] = useState([]);

    const [monthlyGross, setMonthlyGross] = useState("");
    const [annualGross, setAnnualGross] = useState("");

    const [basicPay, setBasicPay] = useState(0);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [totalDeductions, setTotalDeductions] = useState(0);
    const [totalEmployerDeductions, setTotalEmployerDeductions] = useState(0);

    const [netPayMonthly, setNetPayMonthly] = useState(0);
    const [netPayAnnual, setNetPayAnnual] = useState(0);

    const [isFormValid, setIsFormValid] = useState(false);
    const [isCancelClk, setIsCancelClk] = useState(false);

    // PF/ESIC config from API (avoids stale sessionStorage)
    const [salaryConfig, setSalaryConfig] = useState({
        pfLimit: 15000,
        capAmount: 0,
        esicLimitUpto: ESI_ALLOWED_LIMIT,
        salaryDeduction: "gross",
    });
    const monthlyGrossValue = clampGrossMonthly(monthlyGross);

    // variables
    const navigate = useNavigate();
    const location = useLocation();
    const prevPropsRef = useRef();
    const baselineRef = useRef(null);
    const { fetch, update, create } = useApiMethods()
    const { fromEdit, editDetails, isActive } = location.state || {};

    const currentDraftSnapshot = () => JSON.stringify({
        formData: {
            salary_template_name: formData.salary_template_name,
            is_active: formData.is_active,
            is_draft: formData.is_draft,
        },
        selectedDept: (selectedDept || []).map((row) => row.id),
        selectedDesig: (selectedDesig || []).map((row) => row.id),
        earningComponents,
        deductionComponents,
        monthlyGross,
    });

    const isDirty = () => {
        if (!baselineRef.current) {
            return Boolean(
                formData.salary_template_name
                || selectedDept?.length
                || selectedDesig?.length
                || earningComponents?.length
                || deductionComponents?.length
                || monthlyGross,
            );
        }
        return baselineRef.current !== currentDraftSnapshot();
    };

    const validateForm = () => {
        const { salary_template_name } = formData;

        const validEarnings = earningComponents.some(
            (comp) => comp.name?.trim() && parseFloat(comp.percentage) > 0,
        );
        const totalEarningsPercentage = earningComponents?.reduce((acc, curr) => {
            return curr.calcof === "CTC" ? acc + Number(curr.percentage || 0) : acc;
        }, 0);
        const hasBasicComponent = earningComponents.some(item => item.name?.toLowerCase() === "basic");
        const hasDAComponent = earningComponents.some(item => item.name?.toLowerCase() === "da");
        const isTotalCTCPercentValid = Math.abs(totalEarningsPercentage - 100) < 0.01;
        const hasPFInDeductions = deductionComponents?.some(item => item.name?.toLowerCase() === "pf");

        if (!selectedDept?.length) return false;
        if (!selectedDesig?.length) return false;
        if (!validEarnings) return false;
        if (!hasBasicComponent) return false;
        if (!isTotalCTCPercentValid) return false;
        if (!salary_template_name.trim()) return false;
        if (hasPFInDeductions && (!hasBasicComponent || !hasDAComponent)) return false;
        if (monthlyGrossValue > 0 && Number(netPayMonthly) < 0) return false;

        return true;
    };

    useEffect(() => {
        setIsFormValid(validateForm());
    }, [formData.salary_template_name, selectedDept, selectedDesig, earningComponents, deductionComponents, monthlyGrossValue, netPayMonthly]);

    const salaryBreakdown = useMemo(() => {
        if (monthlyGrossValue <= 0 || !earningComponents?.length) {
            return null;
        }
        return calculateSalaryBreakdown(
            monthlyGrossValue,
            earningComponents,
            deductionComponents,
            salaryConfig,
        );
    }, [monthlyGrossValue, earningComponents, deductionComponents, salaryConfig]);

    useEffect(() => {
        if (!salaryBreakdown) {
            setBasicPay(0);
            setTotalEarnings(0);
            setTotalDeductions(0);
            setTotalEmployerDeductions(0);
            setNetPayMonthly(0);
            setNetPayAnnual(0);
            return;
        }
        setBasicPay(salaryBreakdown.basicPay);
        setTotalEarnings(salaryBreakdown.totalEarnings);
        setTotalDeductions(salaryBreakdown.totalDeductions);
        setTotalEmployerDeductions(salaryBreakdown.totalEmployerDeductions);
        setNetPayMonthly(salaryBreakdown.netPayMonthly);
        setNetPayAnnual(salaryBreakdown.netPayAnnual);
    }, [salaryBreakdown]);

    useEffect(() => {
        if (editDetails !== null &&
            Object.keys(editDetails).length !== 0 &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails)) {
            let updateData = {
                is_active: editDetails?.is_active,
                code: editDetails?.code ? editDetails?.code : "",
                salary_template_name: editDetails?.salary_template_name ? editDetails?.salary_template_name : "",
                salary_department: editDetails?.department ? editDetails?.department?.id : "",
                salary_designation: editDetails?.designation ? editDetails?.designation?.id : "",
                earning_components: editDetails?.earning_components ? editDetails?.earning_components : [],
                total_earning: { ...editDetails?.total_earning },
                deduction_components: editDetails?.deduction_components ? editDetails?.deduction_components : [],
                total_deduction: { ...editDetails?.total_deduction },
                net_salary: editDetails?.net_salary ? editDetails?.net_salary : 0,
                is_draft: editDetails?.is_draft ? editDetails?.is_draft : false,
                id: editDetails?.id ? editDetails?.id : 0,
            }

            setSelectedDept(
                (editDetails?.departments?.length ? editDetails.departments : (editDetails?.department ? [editDetails.department] : []))
            )
            setSelectedDesig(
                (editDetails?.designations?.length ? editDetails.designations : (editDetails?.designation ? [editDetails.designation] : []))
            )
            setEarningComponents(editDetails?.earning_components ? editDetails?.earning_components : [])
            setDeductionComponents(
                editDetails?.deduction_components?.map(comp => ({
                    ...comp,
                    monthly_amount: comp.monthly_amount
                        ?? comp.employee_share_monthly_amount
                        ?? 0,
                    employee_share_monthly_amount: comp.employee_share_monthly_amount
                        ?? comp.monthly_amount
                        ?? 0,
                })) || []
            );
            setFormData((prevFormData) => ({
                ...prevFormData,
                id: editDetails.id,
                ...updateData
            }));
            // Preview gross stays local only — use saved value if present, else empty.
            const savedMonthly = editDetails?.monthly_ctc;
            setMonthlyGross(savedMonthly || savedMonthly === 0 ? savedMonthly : "");
            setAnnualGross(editDetails?.annual_ctc || editDetails?.annual_ctc === 0 ? editDetails.annual_ctc : "");
            prevPropsRef.current = editDetails;
            baselineRef.current = JSON.stringify({
                formData: {
                    salary_template_name: updateData.salary_template_name,
                    is_active: updateData.is_active,
                    is_draft: updateData.is_draft,
                },
                selectedDept: (editDetails?.departments?.length
                    ? editDetails.departments
                    : (editDetails?.department ? [editDetails.department] : [])
                ).map((row) => row.id),
                selectedDesig: (editDetails?.designations?.length
                    ? editDetails.designations
                    : (editDetails?.designation ? [editDetails.designation] : [])
                ).map((row) => row.id),
                earningComponents: editDetails?.earning_components || [],
                deductionComponents: editDetails?.deduction_components || [],
                monthlyGross: savedMonthly || savedMonthly === 0 ? savedMonthly : "",
            });
        }
    }, [editDetails])

    const getEmployeeDesignation = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' }); // Fixed: Changed EMPLOYEE_DEPARTMENT to EMPLOYEE_DESIGNATION
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load designations');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getEmployeeAllDepartment = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load departments');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getSalaryComponentList = useCallback(async () => {
        try {
            const apiData = await fetch(SALARY_COMPONENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load salary components');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    // functions
    const fetchDropdownData = async () => {
        setLoadings(true)
        try {
            const [designationRes, departmentRes, salaryComponentRes] = await Promise.all([
                getEmployeeDesignation(),
                getEmployeeAllDepartment(),
                getSalaryComponentList()
            ]);

            const designationList = designationRes?.status === "success" ? designationRes.data : [];
            const departmentlist = departmentRes?.status === "success" ? departmentRes.data : [];

            const allComponents = salaryComponentRes?.status === "success" && Array.isArray(salaryComponentRes?.data)
                ? salaryComponentRes.data
                : [];

            const earningComponents = allComponents.filter(
                (item) => item.component_type === "earning" && (item.classification || "fixed") !== "variable"
            );
            const deductionComponents = allComponents.filter(item => item.component_type === "deduction");

            setDropDownList({
                designationList,
                departmentlist,
                componentList: allComponents,
                earningComponents,
                deductionComponents,
            });

            setLoadings(false)

        } catch (error) {
            ToastMsg('error', "Error loadings dropdowns");
            setDropDownList({
                designationList: [],
                departmentlist: [],
                componentList: [],
                earningComponents: [],
                deductionComponents: [],
            });
        }
        setLoadings(false)
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        const loadCustomConfig = async () => {
            try {
                const response = await fetch(CUSTOM_CONFIGURATION);
                if (response?.status === "success" && response?.data?.[0]) {
                    const d = response.data[0];
                    setSalaryConfig({
                        pfLimit: d.pf_limit ?? 15000,
                        capAmount: d.cap_amount !== undefined && d.cap_amount !== null ? Number(d.cap_amount) : 0,
                        esicLimitUpto: ESI_ALLOWED_LIMIT,
                        salaryDeduction: d.salary_deduction || "gross",
                    });
                }
            } catch (_) {
                const fallback = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
                setSalaryConfig({
                    pfLimit: fallback?.pf_limit ?? 15000,
                    capAmount: fallback?.cap_amount !== undefined && fallback?.cap_amount !== null ? Number(fallback.cap_amount) : 0,
                    esicLimitUpto: ESI_ALLOWED_LIMIT,
                    salaryDeduction: fallback?.salary_deduction || "gross",
                });
            }
        };
        loadCustomConfig();
    }, []);

    // event handlers
    /** Template stores structure only; amounts are computed at employee assignment / payroll. */
    const preparePayload = (formData, earningsInput, deductionsInput, selectedDept, selectedDesig, isdraft = false) => {
        const updatedEarningComponents = (earningsInput || []).map((comp) => ({
            id: comp.id,
            name: comp.name,
            calcof: comp.calcof || "CTC",
            percentage: parseFloat(comp.percentage) || 0,
        }));

        const updatedDeductionComponents = (deductionsInput || []).map((comp) => {
            const isFixed = Boolean(comp.is_amount) || (!comp.is_employee_share && !comp.is_employer_share);
            const fixedMonthly = parseFloat(comp.monthly_amount ?? comp.employee_share_monthly_amount) || 0;
            return {
                id: comp.id,
                name: comp.name,
                calcof: comp.calcof || "CTC",
                is_amount: isFixed,
                is_employee_share: comp.is_employee_share || false,
                is_employer_share: comp.is_employer_share || false,
                employee_share_percentage: parseFloat(comp.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(comp.employer_share_percentage) || 0,
                monthly_amount: isFixed ? fixedMonthly : 0,
                employee_share_monthly_amount: isFixed ? fixedMonthly : 0,
                employer_share_monthly_amount: 0,
            };
        });

        const totalEarningPercentage = updatedEarningComponents.reduce(
            (sum, comp) => (comp.calcof === "CTC" ? sum + Number(comp.percentage || 0) : sum),
            0,
        );
        const totalDeductionPercentage = updatedDeductionComponents.reduce(
            (sum, comp) => sum + Number(comp.employee_share_percentage || 0),
            0,
        );
        const departmentIds = (selectedDept || []).map((row) => row.id || row).filter(Boolean);
        const designationIds = (selectedDesig || []).map((row) => row.id || row).filter(Boolean);

        return {
            id: formData.id,
            code: formData.code,
            is_active: formData.is_active,
            salary_template_name: formData.salary_template_name,
            monthly_ctc: null,
            annual_ctc: null,
            earning_components: updatedEarningComponents,
            total_earning: {
                percentage: totalEarningPercentage,
            },
            deduction_components: updatedDeductionComponents,
            total_deduction: {
                percentage: totalDeductionPercentage,
            },
            net_salary: null,
            department: departmentIds[0] ?? null,
            designation: designationIds[0] ?? null,
            department_ids: departmentIds,
            designation_ids: designationIds,
            is_draft: isdraft,
        };
    };

    // Preview-only gross for sidebar calculations — not persisted on the template.
    const handleMonthlyGrossChange = (value) => {
        const parsed = parseOptionalNumber(value);
        if (parsed === "" || parsed === "-" || parsed === "." || parsed === "-.") {
            setMonthlyGross(parsed === "-" || parsed === "-." ? parsed : "");
            setAnnualGross("");
            return;
        }
        if (Number(parsed) < 0) {
            ToastMsg("error", "Monthly Gross cannot be negative.");
            return;
        }
        const ctc = clampGrossMonthly(parsed);
        setMonthlyGross(value === "" ? "" : ctc);
        setAnnualGross(ctc ? round2(ctc * 12) : "");
    };

    // submit handler
    const handleSubmit = async (isdraft = false) => {
        try {
            if (!isdraft && monthlyGrossValue > 0 && Number(netPayMonthly) < 0) {
                ToastMsg("error", "Net pay is negative. Reduce deductions or increase CTC before saving.");
                return;
            }
            setLoadings(true);

            // Prepare the payload
            const payload = preparePayload(
                formData,
                earningComponents,
                deductionComponents,
                selectedDept,
                selectedDesig,
                isdraft
            );

            // console.log("Submitting payload:", payload);

            // Use the appropriate method from useApiMethods
            if (fromEdit) {
                // For PUT/update
                const response = await update(SALARY_TEMPLATE, payload, false, setLoadings);
                handleApiResponse(response);
            } else {
                // For POST/create
                const response = await create(SALARY_TEMPLATE, payload, false, setLoadings);
                handleApiResponse(response);
            }

        } catch (error) {
            console.error("Submit error:", error);
            ToastMsg("error", error.message || "Failed to create salary template");
            setLoadings(false);
        }
    };

    // Helper function to handle API response
    const handleApiResponse = (data) => {
        if (data?.status === "success") {
            ToastMsg("success", data.message);
            setFormData(initialFormData);
            navigate('/configuration/salarytemplate');
        } else if (data?.status === "fail") {
            ToastMsg("error", data.message);
        } else {
            ToastMsg("error", "Unexpected response from server");
        }
        setLoadings(false);
    };

    const handleInitalCancelBtn = () => {
        if (isDirty()) {
            setIsCancelClk(true);
            return;
        }
        navigate('/configuration/salarytemplate');
    };

    const handleCancelBtn = () => {
        setIsCancelClk(false);
        navigate('/configuration/salarytemplate');
    };

    const handleSaveAsDraft = () => {
        // console.log("Saving as draft...");
        // send the draft value as true
        handleSubmit(true)
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // render functions

    // conditional rendering

    // return JSX

    if (loadings) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard className="dark:bg-gray-900">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    onClick={handleInitalCancelBtn}
                />
                <div className="flex-1">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Salary Template" : "Add Salary Template"}
                    </h1>
                    <p className="text-sm text-muted-foreground dark:text-gray-400 mt-1 mb-1">
                        Define and manage salary structures and compensation templates
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                !fromEdit && !isActive ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={!fromEdit && !isActive}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 shadow-sm",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* template details */}
            <div className="grid grid-cols-[2fr_1fr] gap-4 mt-2">
                {/* Column 1: Three cards stacked vertically */}
                <div className="flex flex-col gap-4 overflow-y-auto h-[calc(100vh-180px)]">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-sidebar-primary dark:bg-sidebar-primary rounded-full"></div>
                            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Template Details</h3>
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            Set up the basic information for this salary template.
                        </span>

                        {/* Form Inputs */}
                        <div className="mt-4 grid grid-cols-1 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Template Name</label>
                                <input
                                    type="text"
                                    placeholder="Enter template name"
                                    value={formData.salary_template_name}
                                    onChange={(e) => setFormData({ ...formData, salary_template_name: e.target.value })}
                                    required
                                    disabled={loadings}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md h-10 px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:border-gray-300 dark:focus:border-gray-600 transition-colors duration-200"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        className="w-full mt-1"
                                        placeholder="Select departments"
                                        value={(selectedDept || []).map((row) => row.id)}
                                        options={(dropdownList?.departmentlist || []).map((row) => ({
                                            value: row.id,
                                            label: row.department_name,
                                        }))}
                                        onChange={(ids) => {
                                            const list = dropdownList?.departmentlist || [];
                                            setSelectedDept(list.filter((row) => ids.includes(row.id)));
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Designation</label>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        className="w-full mt-1"
                                        placeholder="Select designations"
                                        value={(selectedDesig || []).map((row) => row.id)}
                                        options={(dropdownList?.designationList || []).map((row) => ({
                                            value: row.id,
                                            label: row.designation_name,
                                        }))}
                                        onChange={(ids) => {
                                            const list = dropdownList?.designationList || [];
                                            setSelectedDesig(list.filter((row) => ids.includes(row.id)));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* earnings */}
                        <ComponentTable
                            type="Earnings"
                            components={earningComponents}
                            setComponents={setEarningComponents}
                            monthlyGross={monthlyGrossValue}
                            componentList={dropdownList.earningComponents}
                            earningComponents={earningComponents}
                            salaryConfig={salaryConfig}
                        />
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* deductions */}
                        <ComponentTable
                            type="Deductions"
                            components={deductionComponents}
                            setComponents={setDeductionComponents}
                            monthlyGross={monthlyGrossValue}
                            componentList={dropdownList.deductionComponents}
                            earningComponents={earningComponents}
                            salaryConfig={salaryConfig}
                        />
                    </div>
                </div>

                {/* Column 2: One card spanning the full height */}
                <div className="sticky top-4 self-start">
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-2">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-sidebar-primary dark:bg-sidebar-primary rounded-full"></div>
                            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Salary Preview</h3>
                        </div>
                        <span className="text-sm text-gray-400 dark:text-gray-500 mb-4 block">Real-time salary breakdown preview</span>

                        {/* Monthly Gross Input — preview only, not saved on template */}
                        <div className="mb-3">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                Monthly Gross <span className="text-xs font-normal text-gray-400">(preview only)</span>
                            </label>
                            <input
                                type="number"
                                value={monthlyGross === 0 || monthlyGross ? monthlyGross : ""}
                                onChange={(e) => handleMonthlyGrossChange(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md h-10 px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:border-gray-300 dark:focus:border-gray-600 transition-colors duration-200"
                                placeholder="Enter Monthly Gross"
                            />
                        </div>

                        <div className='flex justify-between items-center mb-3 text-gray-600 dark:text-gray-400'>
                            <span className='flex gap-2 items-center'>
                                <label className="text-sm font-medium">Monthly CTC</label>
                                <Tooltip title={
                                    <span className='text-gray-100 text-xs'>
                                        CTC = Gross + Employer Contributions (PF + ESIC + Others)
                                    </span>
                                }>
                                    <TriangleAlert className="text-gray-400 dark:text-gray-500 h-3 w-3 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200" />
                                </Tooltip>
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                ₹{round2(monthlyGrossValue + totalEmployerDeductions).toFixed(2)}
                            </span>
                        </div>

                        {/* Annual Gross Input */}
                        <div className="mb-3">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Annual Gross</label>
                            <input
                                type="number"
                                value={annualGross === 0 || annualGross ? Number(annualGross).toFixed(2) : ""}
                                readOnly
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md h-10 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white border-dashed cursor-not-allowed"
                                placeholder="Annual Gross"
                            />
                        </div>

                        <div className='flex justify-between items-center mb-4 text-gray-600 dark:text-gray-400'>
                            <span className='flex gap-2 items-center'>
                                <label className="text-sm font-medium">Annual CTC</label>
                                <Tooltip title={
                                    <span className='text-gray-100 text-xs'>
                                        CTC = Gross + Employer Contributions (PF + ESIC + Others)
                                    </span>
                                }>
                                    <TriangleAlert className="text-gray-400 dark:text-gray-500 h-3 w-3 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200" />
                                </Tooltip>
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                ₹{round2((monthlyGrossValue + totalEmployerDeductions) * 12).toFixed(2)}
                            </span>
                        </div>

                        <hr className="my-4 border-gray-200 dark:border-gray-600" />

                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Salary Summary</h4>

                        <div className="text-sm space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Basic Pay:</span>
                                <span className="font-medium text-gray-900 dark:text-white">₹{basicPay?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Total Earnings:</span>
                                <span className="font-medium text-green-600 dark:text-green-400">₹{totalEarnings?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                                <span className="font-medium text-red-600 dark:text-red-400">₹{totalDeductions?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
                                <span className="font-semibold text-gray-900 dark:text-white">Net Pay (Monthly):</span>
                                <span className={cn(
                                    "font-bold",
                                    Number(netPayMonthly) < 0
                                        ? "text-red-600 dark:text-red-400"
                                        : "text-gray-900 dark:text-white"
                                )}>₹{netPayMonthly?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-900 dark:text-white">Net Pay (Annual):</span>
                                <span className="font-bold text-gray-900 dark:text-white">₹{netPayAnnual?.toFixed(2) || '0.00'}</span>
                            </div>
                            {Number(netPayMonthly) < 0 && (
                                <p className="text-xs text-red-600 dark:text-red-400 pt-1">
                                    Net pay is negative. The template cannot be saved until deductions are below CTC.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex bg-white dark:bg-gray-800 rounded-lg justify-end gap-3 p-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <Button
                            variant="outline"
                            onClick={handleInitalCancelBtn}
                            disabled={loadings}
                            className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </Button>

                        {/* Draft Button - Only show in create mode */}
                        {!fromEdit && (
                            <Button
                                onClick={handleSaveAsDraft}
                                disabled={loadings || !isFormValid}
                                className={cn(
                                    "bg-gray-600 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-700",
                                    "text-white transition-colors duration-200",
                                    "flex items-center gap-2",
                                    (loadings || !isFormValid) && "opacity-50 cursor-not-allowed"
                                )}
                            >
                                Save as Draft
                            </Button>
                        )}

                        <Button
                            variant="brand"
                            onClick={() => handleSubmit(false)}
                            disabled={loadings || !isFormValid}
                            className={cn(
                                "transition-colors duration-200",
                                "flex items-center gap-2",
                                (loadings || !isFormValid) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            {loadings ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {loadings ? "Saving..." : (fromEdit ? "Update" : "Save Template")}
                        </Button>
                    </div>
                </div>
            </div>
            <ConfirmPopupModal
                open={isCancelClk}
                onClose={() => setIsCancelClk(false)}
                onConfirm={handleCancelBtn}
                title="Discard changes?"
                description="You have unsaved salary template changes. Leave this page and discard them?"
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                type="warning"
                destructive
            />
        </ContainerCard>
    );
}

export default SalaryTemplateAddEdit;